import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Star, Truck, ShieldCheck, Check, ShoppingCart, Heart, Repeat2, Share2, RotateCcw, Zap } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { ProductImage } from "@/components/shop/ProductImage";
import { ProductCard } from "@/components/shop/ProductCard";
import { PurchaseOptionDialog } from "@/components/shop/PurchaseOptionDialog";
import { getProductBySlug } from "@/lib/shop.functions";
import {
  PHYSIO_INCLUDES,
  STANDARD_INCLUDES,
  formatPrice,
  standardDisplayPrice,
  stockLabel,
  type Product,
} from "@/lib/shop";
import { useCompare, useRecentlyViewed, useWishlist } from "@/lib/shop-prefs";

export const Route = createFileRoute("/shop/$slug")({
  loader: async ({ params }) => {
    const res = await getProductBySlug({ data: { slug: params.slug } });
    if (!res.product) throw notFound();
    return res;
  },
  head: ({ loaderData }) => {
    const p = loaderData?.product;
    if (!p) return {};
    const title = p.seo_title || `${p.name} | N S Physiotherapy Shop`;
    const description =
      p.seo_description || p.short_description || `${p.name} available from N S Physiotherapy in Centurion.`;
    const url = `https://nsphysiotherapy.co.za/shop/${p.slug}`;
    const image = p.images?.[0];
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(image && image.startsWith("https://")
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: p.name,
            sku: p.sku || undefined,
            brand: p.brand ? { "@type": "Brand", name: p.brand } : undefined,
            description,
            image: image || undefined,
            offers: {
              "@type": "Offer",
              priceCurrency: "ZAR",
              price: standardDisplayPrice(p),
              itemCondition: "https://schema.org/NewCondition",
              availability:
                p.stock_quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              url,
              hasMerchantReturnPolicy: {
                "@type": "MerchantReturnPolicy",
                applicableCountry: "ZA",
                returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
                merchantReturnDays: 7,
                returnMethod: "https://schema.org/ReturnByMail",
                returnFees: "https://schema.org/ReturnShippingFees",
                returnShippingFeesAmount: {
                  "@type": "MonetaryAmount",
                  currency: "ZAR",
                  value: 95,
                },
              },
              shippingDetails: {
                "@type": "OfferShippingDetails",
                shippingRate: {
                  "@type": "MonetaryAmount",
                  value: 95,
                  currency: "ZAR",
                },
                shippingDestination: {
                  "@type": "DefinedRegion",
                  addressCountry: "ZA",
                },
                deliveryTime: {
                  "@type": "ShippingDeliveryTime",
                  handlingTime: {
                    "@type": "QuantitativeValue",
                    minValue: 1,
                    maxValue: 2,
                    unitCode: "DAY",
                  },
                  transitTime: {
                    "@type": "QuantitativeValue",
                    minValue: 2,
                    maxValue: 5,
                    unitCode: "DAY",
                  },
                },
              },
            },

          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", url: "https://nsphysiotherapy.co.za/" },
              { name: "Shop", url: "https://nsphysiotherapy.co.za/shop" },
              { name: p.name, url },
            ]),
          ),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <section className="container-page py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-primary">Product not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">This product may have been removed or is not yet published.</p>
        <Link to="/shop" className="btn-hero mt-6 inline-flex h-11 items-center rounded-full px-6 text-sm font-semibold">
          Back to shop
        </Link>
      </section>
    </PageShell>
  ),
  errorComponent: ({ error }) => (
    <PageShell>
      <section className="container-page py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-primary">Something went wrong</h1>
        <p className="mt-3 text-sm text-muted-foreground">{(error as Error).message}</p>
      </section>
    </PageShell>
  ),
  component: ProductPage,
});

function ProductPage() {
  const { product, related, boughtTogether } = Route.useLoaderData() as {
    product: Product;
    related: Product[];
    boughtTogether: Product[];
  };
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [dialogProduct, setDialogProduct] = useState<Product | null>(null);
  const [buyNow, setBuyNow] = useState(false);
  const [shared, setShared] = useState(false);
  const recent = useRecentlyViewed();
  const pushRecent = recent.push;

  useEffect(() => {
    pushRecent(product.id);
  }, [product.id, pushRecent]);

  async function share() {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: product.name, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch {
      /* user dismissed the share sheet */
    }
  }
  const wishlist = useWishlist();
  const compare = useCompare();

  const stock = stockLabel(product);
  const physio = product.physio_purchase_enabled && product.physio_price != null ? product.physio_price : null;
  const hasRecommendation =
    product.recommendation_why ||
    product.recommendation_conditions ||
    product.recommendation_advice ||
    product.recommendation_precautions ||
    product.recommendation_tips;

  return (
    <PageShell>
      <Breadcrumbs
        items={[{ label: "Home", to: "/" }, { label: "Shop", to: "/shop" }, { label: product.name }]}
      />

      <section className="container-page py-8">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <div
              className={`overflow-hidden rounded-2xl border border-border bg-secondary/30 ${zoom ? "cursor-zoom-out" : "cursor-zoom-in"}`}
              onClick={() => setZoom((z) => !z)}
            >
              <ProductImage
                src={product.images?.[active]}
                alt={product.name}
                eager
                className={`h-[320px] w-full p-6 transition-transform duration-300 md:h-[460px] ${zoom ? "scale-150" : "scale-100"}`}
              />
            </div>
            {product.images?.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {product.images.map((img, i) => (
                  <button
                    key={img + i}
                    onClick={() => setActive(i)}
                    aria-label={`View image ${i + 1}`}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border ${i === active ? "border-primary" : "border-border"}`}
                  >
                    <ProductImage src={img} alt={`${product.name} view ${i + 1}`} className="h-full w-full p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            {product.brand && (
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{product.brand}</div>
            )}
            <h1 className="mt-2 font-display text-2xl font-bold text-primary md:text-3xl">{product.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {product.sku && <span>SKU {product.sku}</span>}
              {product.category && <span>· {product.subcategory || product.category}</span>}
              <span className={stock.ok ? "" : "text-destructive"}>· {stock.label}</span>
            </div>
            {product.physio_recommended && (
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <Star className="h-3.5 w-3.5 fill-primary" /> Physiotherapist Recommended
              </span>
            )}

            {product.short_description && (
              <p className="mt-4 text-sm text-foreground/75">{product.short_description}</p>
            )}

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <PriceBox
                title="Standard Price"
                price={standardDisplayPrice(product)}
                strike={product.on_sale && product.sale_price != null ? product.standard_price : undefined}
                includes={STANDARD_INCLUDES}
              />
              {physio != null && (
                <PriceBox recommended title="Physio Guided Price" price={physio} includes={PHYSIO_INCLUDES} />
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!stock.ok}
                onClick={() => { setBuyNow(false); setDialogProduct(product); }}
                className="btn-hero inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold disabled:opacity-50"
              >
                <ShoppingCart className="h-4 w-4" /> Add to Cart
              </button>
              <button
                type="button"
                disabled={!stock.ok}
                onClick={() => { setBuyNow(true); setDialogProduct(product); }}
                className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full border border-primary px-6 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
              >
                <Zap className="h-4 w-4" /> Buy Now
              </button>
              <button
                type="button"
                onClick={() => wishlist.toggle(product.id)}
                aria-pressed={wishlist.has(product.id)}
                className="inline-flex h-12 items-center gap-2 rounded-full border border-border px-5 text-sm font-semibold text-primary hover:bg-accent"
              >
                <Heart className={`h-4 w-4 ${wishlist.has(product.id) ? "fill-primary" : ""}`} /> Wishlist
              </button>
              <button
                type="button"
                onClick={() => compare.toggle(product.id)}
                aria-pressed={compare.has(product.id)}
                className="inline-flex h-12 items-center gap-2 rounded-full border border-border px-5 text-sm font-semibold text-primary hover:bg-accent"
              >
                <Repeat2 className="h-4 w-4" /> Compare
              </button>
              <button
                type="button"
                onClick={share}
                className="inline-flex h-12 items-center gap-2 rounded-full border border-border px-5 text-sm font-semibold text-primary hover:bg-accent"
              >
                <Share2 className="h-4 w-4" /> {shared ? "Link copied" : "Share"}
              </button>
            </div>

            <ul className="mt-5 grid gap-3 sm:grid-cols-3">
              <TrustItem icon={Truck} title="Fast delivery" text="Nationwide courier, 2 to 4 working days" />
              <TrustItem icon={ShieldCheck} title="Secure payment" text="Card and Instant EFT via iKhokha" />
              <TrustItem icon={RotateCcw} title="Easy returns" text="7 day returns on unused items" />
            </ul>

            {product.delivery_info && (
              <div className="mt-5 flex items-start gap-2 rounded-xl border border-border p-4 text-xs text-muted-foreground">
                <Truck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <div className="font-semibold text-primary">Delivery information</div>
                  <p className="mt-1 whitespace-pre-line">{product.delivery_info}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {(product.description || product.features?.length > 0) && (
        <section className="container-page pb-10">
          <div className="grid gap-8 md:grid-cols-2">
            {product.description && (
              <Block title="Description">
                <p className="whitespace-pre-line text-sm text-foreground/75">{product.description}</p>
              </Block>
            )}
            {product.features?.length > 0 && (
              <Block title="Features">
                <ul className="space-y-2">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground/75">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {f}
                    </li>
                  ))}
                </ul>
              </Block>
            )}
          </div>
        </section>
      )}

      {(product.specifications?.length > 0 || product.suitable_for?.length > 0) && (
        <section className="container-page pb-10">
          <div className="grid gap-8 md:grid-cols-2">
            {product.specifications?.length > 0 && (
              <Block title="Specifications">
                <dl className="divide-y divide-border text-sm">
                  {product.specifications.map((s) => (
                    <div key={s.label} className="flex justify-between gap-4 py-2">
                      <dt className="text-muted-foreground">{s.label}</dt>
                      <dd className="text-right font-medium text-primary">{s.value}</dd>
                    </div>
                  ))}
                </dl>
              </Block>
            )}
            {product.suitable_for?.length > 0 && (
              <Block title="Suitable for">
                <ul className="flex flex-wrap gap-2">
                  {product.suitable_for.map((s) => (
                    <li key={s} className="rounded-full border border-border px-3 py-1 text-xs text-foreground/75">
                      {s}
                    </li>
                  ))}
                </ul>
              </Block>
            )}
          </div>
        </section>
      )}

      {hasRecommendation && (
        <section className="container-page pb-12">
          <div className="rounded-2xl border border-border bg-secondary/30 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold text-primary">
              <ShieldCheck className="h-5 w-5" /> Recommended by N S Physiotherapy
            </h2>
            <div className="mt-5 grid gap-5 md:grid-cols-2">
              <Note title="Why we recommend it" body={product.recommendation_why} />
              <Note title="Suitable conditions" body={product.recommendation_conditions} />
              <Note title="Clinical advice" body={product.recommendation_advice} />
              <Note title="Precautions" body={product.recommendation_precautions} />
              <Note title="Rehabilitation tips" body={product.recommendation_tips} />
            </div>
          </div>
        </section>
      )}

      {boughtTogether.length > 0 && (
        <ProductRow title="Frequently bought together" items={boughtTogether} onAdd={setDialogProduct} />
      )}
      {related.length > 0 && <ProductRow title="Complete your recovery" items={related} onAdd={setDialogProduct} />}

      <PurchaseOptionDialog
        product={dialogProduct}
        open={!!dialogProduct}
        buyNow={buyNow}
        onOpenChange={(v) => !v && setDialogProduct(null)}
      />
    </PageShell>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-lg font-bold text-primary">{title}</h2>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Note({ title, body }: { title: string; body: string | null }) {
  if (!body) return null;
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-primary/70">{title}</h3>
      <p className="mt-1.5 whitespace-pre-line text-sm text-foreground/75">{body}</p>
    </div>
  );
}

function PriceBox({
  title,
  price,
  includes,
  recommended,
  strike,
}: {
  title: string;
  price: number;
  includes: string[];
  recommended?: boolean;
  strike?: number;
}) {
  return (
    <div className={`rounded-2xl border p-4 ${recommended ? "border-primary/40 bg-secondary/40" : "border-border"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary/70">
          {recommended && <Star className="h-3.5 w-3.5 fill-primary text-primary" />}
          {title}
        </span>
        {recommended && (
          <span className="rounded-full border border-primary/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
            Recommended
          </span>
        )}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display text-xl font-bold text-primary">{formatPrice(price)}</span>
        {strike != null && <span className="text-xs text-muted-foreground line-through">{formatPrice(strike)}</span>}
      </div>
      <ul className="mt-3 space-y-1">
        {includes.map((i) => (
          <li key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
            {i}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProductRow({ title, items, onAdd }: { title: string; items: Product[]; onAdd: (p: Product) => void }) {
  return (
    <section className="container-page pb-12">
      <h2 className="font-display text-xl font-bold text-primary">{title}</h2>
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((p) => (
          <ProductCard key={p.id} product={p} onAdd={onAdd} />
        ))}
      </div>
    </section>
  );
}

function TrustItem({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof Truck;
  title: string;
  text: string;
}) {
  return (
    <li className="flex items-start gap-2 rounded-xl border border-border p-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0">
        <div className="text-xs font-semibold text-primary">{title}</div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">{text}</p>
      </div>
    </li>
  );
}
